// Auto Color — neutralizes the image's color cast, then applies Auto Contrast.
//
// Algorithm (matching Photoshop):
//   1. Compute the average color of the image (mean R, G, B)
//   2. Find the neutral target: the midpoint (128, 128, 128)
//   3. Compute the shift needed to push the average toward neutral
//   4. Apply that shift to every pixel (reducing color cast)
//   5. Apply Auto Contrast (luminance-based stretch with 0.1% clipping)
//
// The color neutralization uses a "soft" approach: it pushes the average
// toward gray but preserves some of the original cast (Photoshop's Auto
// Color only partially neutralizes — about 50% correction).

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface AutoColorParams {
  /** Fraction of pixels to clip from each end for contrast stretch (default 0.001) */
  clip: number;
  /** How much to neutralize the color cast (0 = none, 1 = full). Default 0.5 */
  neutralize: number;
}

export const AutoColor: Filter<AutoColorParams> = {
  id: "auto-color",
  displayName: "Auto Color",
  defaultParams: { clip: 0.001, neutralize: 0.5 },

  apply(input: PixelBuffer, params: AutoColorParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    // Step 1: Compute average color
    let sumR = 0, sumG = 0, sumB = 0;
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      sumR += src[o]!;
      sumG += src[o + 1]!;
      sumB += src[o + 2]!;
    }
    const avgR = sumR / n;
    const avgG = sumG / n;
    const avgB = sumB / n;

    // Step 2: Compute shift toward neutral
    const neut = 128;
    const factor = clampRange(params.neutralize, 0, 1);
    const shiftR = (neut - avgR) * factor;
    const shiftG = (neut - avgG) * factor;
    const shiftB = (neut - avgB) * factor;

    // Apply neutralization to a temporary buffer
    const tmp = new Uint8ClampedArray(src);
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      tmp[o] = clampByte(src[o]! + shiftR);
      tmp[o + 1] = clampByte(src[o + 1]! + shiftG);
      tmp[o + 2] = clampByte(src[o + 2]! + shiftB);
    }

    // Step 3: Auto Contrast on the neutralized data
    const hist = new Uint32Array(256);
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const lum = (tmp[o]! * 77 + tmp[o + 1]! * 150 + tmp[o + 2]! * 29) >> 8;
      hist[lum] = (hist[lum] ?? 0) + 1;
    }

    const total = n;
    const clipFrac = clampRange(params.clip, 0, 0.5);
    const threshold = Math.round(total * clipFrac);
    const minVal = findPercentile(hist, threshold);
    const maxVal = findPercentile(hist, total - threshold);
    const lut = buildStretchLut(minVal, maxVal);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      dst[o] = lut[tmp[o]!]!;
      dst[o + 1] = lut[tmp[o + 1]!]!;
      dst[o + 2] = lut[tmp[o + 2]!]!;
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};

function findPercentile(hist: Uint32Array, target: number): number {
  let sum = 0;
  for (let i = 0; i < 256; i++) {
    sum += hist[i] ?? 0;
    if (sum >= target) return i;
  }
  return 255;
}

function buildStretchLut(minVal: number, maxVal: number): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  if (maxVal <= minVal) { lut.fill(0); return lut; }
  const scale = 255 / (maxVal - minVal);
  for (let v = 0; v < 256; v++) {
    if (v <= minVal) lut[v] = 0;
    else if (v >= maxVal) lut[v] = 255;
    else lut[v] = Math.round((v - minVal) * scale);
  }
  return lut;
}

function clampByte(v: number): number {
  if (!isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 255) return 255;
  return Math.round(v);
}

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
